import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { useNavigate } from "react-router-dom";
import * as categoriesCrud from "../../_redux/categories/categoriesCrud";

// Module-level cache so categories are fetched at most once
let cachedCategories = null;
let pendingFetch = null;

export function AddCategoryModal({
  show,
  onHide,
  onCategoryCreated,
  categories: propCategories = [],
  initialCategoryName = "",
  mode = "business", // "business" or "category"
}) {
  const navigate = useNavigate();
  const [categoriesList, setCategoriesList] = useState(() => {
    if (propCategories && propCategories.length > 0) return propCategories;
    if (cachedCategories && cachedCategories.length > 0) return cachedCategories;
    return [];
  });
  const [loadingList, setLoadingList] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isNewCreation, setIsNewCreation] = useState(false);
  const [typedName, setTypedName] = useState(initialCategoryName || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync / fetch categories once when modal opens
  useEffect(() => {
    if (!show) return;

    setErrorMsg("");
    setSuccessMsg("");
    setIsSubmitting(false);

    if (initialCategoryName) {
      setTypedName(initialCategoryName);
      setSelectedOption({ value: initialCategoryName, label: initialCategoryName });
      setIsNewCreation(true);
    } else {
      setTypedName("");
      setSelectedOption(null);
      setIsNewCreation(false);
    }

    // If categories passed from parent, use and cache them
    if (propCategories && propCategories.length > 0) {
      cachedCategories = propCategories;
      setCategoriesList(propCategories);
      return;
    }

    // If already cached, use cache
    if (cachedCategories && cachedCategories.length > 0) {
      setCategoriesList(cachedCategories);
      return;
    }

    // Otherwise fetch once
    if (!pendingFetch) {
      setLoadingList(true);
      pendingFetch = categoriesCrud
        .getAllCategories()
        .then((res) => {
          const list = res.data?.data || res.data?.entities || [];
          cachedCategories = list;
          pendingFetch = null;
          return list;
        })
        .catch((err) => {
          pendingFetch = null;
          console.error("Error loading categories in modal:", err);
          return [];
        });
    }

    pendingFetch.then((list) => {
      if (isMountedRef.current) {
        setCategoriesList(list);
        setLoadingList(false);
      }
    });
  }, [show, initialCategoryName]); // Notice: do not put propCategories in dependency array to avoid infinite loops

  const options = useMemo(() => {
    return (categoriesList || []).map((c) => ({
      value: String(c.id),
      label: c.name,
      raw: c,
    }));
  }, [categoriesList]);

  // Handle selection from dropdown
  const handleSelectChange = (opt) => {
    setSelectedOption(opt);
    setErrorMsg("");
    if (opt && opt.raw) {
      setIsNewCreation(false);
      setTypedName(opt.raw.name);
    } else if (opt) {
      setIsNewCreation(true);
      setTypedName(opt.label);
    } else {
      setIsNewCreation(false);
      setTypedName("");
    }
  };

  // Handle when user types and chooses "+ Create new category"
  const handleCreateOption = (inputValue) => {
    const trimmed = (inputValue || "").trim();
    if (!trimmed) return;

    // Check if an existing category matches exactly
    const existing = (categoriesList || []).find(
      (c) => (c.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    );

    if (existing) {
      setSelectedOption({
        value: String(existing.id),
        label: existing.name,
        raw: existing,
      });
      setIsNewCreation(false);
      setTypedName(existing.name);
    } else {
      const newOpt = { value: trimmed, label: trimmed };
      setSelectedOption(newOpt);
      setIsNewCreation(true);
      setTypedName(trimmed);
    }
    setErrorMsg("");
  };

  const handleConfirm = async () => {
    if (!selectedOption) {
      setErrorMsg("Please select or type a category name.");
      return;
    }

    // If an existing category is selected
    if (selectedOption.raw) {
      const existing = selectedOption.raw;
      if (mode === "business" && onCategoryCreated) {
        onCategoryCreated({
          id: String(existing.id),
          name: existing.name,
        });
        onHide();
      } else {
        onHide();
        navigate(`/category/${existing.id}/edit`);
      }
      return;
    }

    // Creating a new category
    const trimmed = (typedName || selectedOption.label || "").trim();
    if (!trimmed) {
      setErrorMsg("Category name cannot be empty.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMsg("");

      const res = await categoriesCrud.createCategoryByName(trimmed);
      let createdCat = res.data?.category || res.data?.data || res.data;
      let newId = createdCat?.id ? String(createdCat.id) : null;

      if (!newId) {
        const catRes = await categoriesCrud.getAllCategories();
        const list = catRes.data?.data || catRes.data?.entities || [];
        cachedCategories = list;
        const found = list.find(
          (c) => (c.name || "").toLowerCase() === trimmed.toLowerCase()
        );
        if (found) {
          createdCat = found;
          newId = String(found.id);
        }
      } else {
        // Add to memory cache
        if (cachedCategories) {
          cachedCategories = [
            ...cachedCategories,
            { id: newId, name: trimmed, status: "active", approval_status: "approved" },
          ];
        }
      }

      setSuccessMsg(`Category "${trimmed}" created successfully!`);

      if (onCategoryCreated && newId) {
        onCategoryCreated({
          id: newId,
          name: trimmed,
        });
      }

      setTimeout(() => {
        if (isMountedRef.current) {
          setIsSubmitting(false);
          onHide();
        }
      }, 500);
    } catch (err) {
      console.error("Failed to create category:", err);
      setIsSubmitting(false);
      setErrorMsg(
        "Failed to create category: " +
          (err.response?.data?.message || err.message)
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title className="font-weight-bold d-flex align-items-center">
          <i className="flaticon2-plus text-primary mr-2 font-size-h4"></i>
          <span>Add Category</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-6">
        {errorMsg && (
          <div className="alert alert-custom alert-light-danger p-3 mb-4 font-size-sm">
            <div className="alert-icon">
              <i className="flaticon-warning text-danger"></i>
            </div>
            <div className="alert-text font-weight-bold">{errorMsg}</div>
          </div>
        )}

        {successMsg && (
          <div className="alert alert-custom alert-light-success p-3 mb-4 font-size-sm">
            <div className="alert-icon">
              <i className="flaticon2-checkmark text-success"></i>
            </div>
            <div className="alert-text font-weight-bold">{successMsg}</div>
          </div>
        )}

        <div className="form-group mb-4">
          <label className="font-weight-bold text-dark mb-2">Category Name</label>
          <CreatableSelect
            className="basic-single"
            classNamePrefix="select"
            isClearable
            options={options}
            value={selectedOption}
            onChange={handleSelectChange}
            onCreateOption={handleCreateOption}
            placeholder="Type category name to search or create..."
            isLoading={loadingList}
            formatCreateLabel={(inputValue) => `+ Create new category "${inputValue}"`}
            noOptionsMessage={({ inputValue }) =>
              !inputValue
                ? "Type to search previously added categories..."
                : `No existing category matching "${inputValue}". Press Enter to create.`
            }
            autoFocus
          />
          <span className="form-text text-muted font-size-xs mt-1">
            Start typing to see matching existing categories. If not found, select "+ Create new category" to add it.
          </span>
        </div>

        {/* Existing category selected state */}
        {selectedOption && selectedOption.raw && (
          <div className="alert alert-custom alert-light-primary p-3 mb-2 font-size-sm">
            <div className="alert-text">
              <div className="font-weight-bold mb-1">
                Existing category found: <strong>"{selectedOption.raw.name}"</strong>
              </div>
              <div className="d-flex align-items-center font-size-xs text-muted">
                <span className="badge badge-light-dark mr-2">
                  ID: #{selectedOption.raw.id}
                </span>
                <span
                  className={`badge mr-2 ${
                    selectedOption.raw.status === "active"
                      ? "badge-light-success"
                      : "badge-light-danger"
                  }`}
                >
                  {selectedOption.raw.status || "active"}
                </span>
                {selectedOption.raw.approval_status && (
                  <span className="badge badge-light-info">
                    {selectedOption.raw.approval_status}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New category to be created state */}
        {selectedOption && isNewCreation && (
          <div className="alert alert-custom alert-light-success p-3 mb-2 font-size-sm">
            <div className="alert-icon">
              <i className="flaticon2-plus text-success"></i>
            </div>
            <div className="alert-text font-weight-bold">
              New category to create: <strong>"{typedName}"</strong>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-light font-weight-bold"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary font-weight-bold"
          onClick={handleConfirm}
          disabled={isSubmitting || !selectedOption}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : isNewCreation ? (
            <>
              <i className="fa fa-plus mr-1"></i> Create Category
            </>
          ) : mode === "business" ? (
            <>
              <i className="fa fa-check mr-1"></i> Add to Business
            </>
          ) : (
            <>
              <i className="flaticon2-pen mr-1"></i> Edit Category
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
